import { useMemo, useSyncExternalStore } from 'react';
import { LS_KEY_AI_ASSISTANT_DAY } from 'src/constants/localStorageKeys';
import type { AssistantMoment } from 'src/helpers/assistantMoments';
import type { AssistantNudge } from 'src/redux/types/assistant';

/**
 * The assistant's day in this browser: when the panel was opened and the orb
 * last prompted, which nudges it has shown, and the plan targets the user
 * ticked off. One record, reset when the day or the account changes, shared
 * by every tab so two tabs never both prompt.
 */
export interface AssistantDay {
  userId: string;
  /** The platform day (IST), the same day the backend's plan and cap use. */
  day: string;
  openedAt?: number;
  promptedAt?: number;
  lastClosedAt?: number;
  /** When the orb last asked about a moment, found something to say or not. */
  nudgeAt?: number;
  /** What the user just did, waiting for the orb to speak up about it. */
  moment?: (AssistantMoment & { at: number }) | null;
  /** Nudge keys shown today. */
  shown: string[];
  /** What the orb said that the panel hasn't been opened on yet: its unread dot. */
  pending?: AssistantNudge | null;
  /** Today's plan steps the user ticked off. */
  ticked: string[];
}

const IST_DAY = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata' });
// A same-tab write: the storage event only reaches the other tabs.
const CHANGED = 'abekus-assistant-day';

/** yyyy-MM-dd in IST. */
export const istDay = (at: Date | number | string = Date.now()) =>
  IST_DAY.format(new Date(at));

const readRaw = () => {
  try {
    return localStorage.getItem(LS_KEY_AI_ASSISTANT_DAY) ?? '';
  } catch {
    return '';
  }
};

const parse = (raw: string, userId: string): AssistantDay => {
  const fresh = { userId, day: istDay(), shown: [], ticked: [] };
  try {
    const saved = JSON.parse(raw || 'null') as AssistantDay | null;
    if (saved?.userId === userId && saved.day === fresh.day)
      return { ...fresh, ...saved };
  } catch {
    // Unreadable: start the day over.
  }
  return fresh;
};

export const readDay = (userId: string) => parse(readRaw(), userId);

export const updateDay = (
  userId: string,
  change: (day: AssistantDay) => Partial<AssistantDay>,
) => {
  const day = readDay(userId);
  try {
    localStorage.setItem(
      LS_KEY_AI_ASSISTANT_DAY,
      JSON.stringify({ ...day, ...change(day) }),
    );
  } catch {
    // Blocked storage: the orb may prompt again next page load, nothing worse.
  }
  window.dispatchEvent(new Event(CHANGED));
};

const subscribe = (onChange: () => void) => {
  const onStorage = (e: StorageEvent) => {
    if (e.key === LS_KEY_AI_ASSISTANT_DAY) onChange();
  };
  window.addEventListener('storage', onStorage);
  window.addEventListener(CHANGED, onChange);
  return () => {
    window.removeEventListener('storage', onStorage);
    window.removeEventListener(CHANGED, onChange);
  };
};

/** Today's record, kept current across tabs. */
export const useAssistantDay = (userId: string) => {
  const raw = useSyncExternalStore(subscribe, readRaw);
  return useMemo(() => parse(raw, userId), [raw, userId]);
};

/** A plan target is done once ticked off; the extension's, once it is here. */
export const isStepDone = (
  key: string,
  day: AssistantDay,
  hasExtension: boolean,
) => day.ticked.includes(key) || (key === 'extension' && hasExtension);
